/**
 * SupplierBenchmarkPanel — Supplier S5 (KPI drill-down + peer benchmarking).
 *
 * Drill into one supplier and benchmark each of its 8 KPIs against the peer
 * cohort average (direction-aware: green when better than peers, red when
 * worse), plus its aggregate-score percentile rank. Pure client-side off the
 * KPI-grid rows already loaded — no extra request.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatCard, Stagger } from '@edms/design-system';
import styles from './SupplierKpiDashboard.module.css';
import type { KpiDashboardRow } from './useKpiDashboardQuery';
import type { SupplierKpiKey } from '../SupplierDetail/useSupplierDetailQuery';

const KPI_KEYS: SupplierKpiKey[] = [
  'OTIF', 'DELIVERY_PERFORMANCE', 'DEVIATION_COUNT', 'COMPLAINT_COUNT',
  'OOS_RATE', 'AUDIT_SCORE', 'CAPA_EFFECTIVENESS', 'QUALITY_INCIDENTS',
];
// Higher-is-better KPIs; the rest are lower-is-better.
const HIGHER_BETTER = new Set<SupplierKpiKey>(['OTIF', 'DELIVERY_PERFORMANCE', 'AUDIT_SCORE', 'CAPA_EFFECTIVENESS']);

export interface SupplierBenchmarkPanelProps {
  rows: KpiDashboardRow[];
}

export function SupplierBenchmarkPanel({ rows }: SupplierBenchmarkPanelProps) {
  const { t, i18n } = useTranslation();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }), [i18n.language]);
  const [selId, setSelId] = useState<string>('');

  // Default to the highest-risk (lowest aggregate) supplier so the drill-down
  // opens on the one that most needs attention.
  const selected = useMemo(() => {
    if (selId) return rows.find((r) => r.supplier_id === selId) ?? rows[0];
    const withScore = rows.filter((r) => r.aggregate_quality_score != null);
    if (withScore.length === 0) return rows[0];
    return withScore.reduce((lo, r) => ((r.aggregate_quality_score ?? 999) < (lo.aggregate_quality_score ?? 999) ? r : lo));
  }, [rows, selId]);

  const peerAvg = useMemo(() => {
    const out: Partial<Record<SupplierKpiKey, number>> = {};
    for (const k of KPI_KEYS) {
      const vals = rows.map((r) => r.kpis[k]?.value).filter((v): v is number => v != null);
      if (vals.length) out[k] = vals.reduce((a, b) => a + b, 0) / vals.length;
    }
    return out;
  }, [rows]);

  const aggStats = useMemo(() => {
    const scores = rows.map((r) => r.aggregate_quality_score).filter((v): v is number => v != null).sort((a, b) => a - b);
    const peer = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : null;
    const sel = selected?.aggregate_quality_score ?? null;
    let percentile: number | null = null;
    if (sel != null && scores.length) {
      const below = scores.filter((s) => s < sel).length;
      percentile = Math.round((below / scores.length) * 100);
    }
    return { peer, sel, percentile };
  }, [rows, selected]);

  if (!selected) return null;

  return (
    <section className={styles.benchPanel} aria-label={t('supplier.kpi.bench.aria', { defaultValue: 'Peer benchmarking drill-down' })}>
      <div className={styles.benchHead}>
        <div>
          <h3 className={styles.benchTitle}>{t('supplier.kpi.bench.title', { defaultValue: 'Peer benchmark drill-down' })}</h3>
          <span className={styles.benchSub}>{t('supplier.kpi.bench.sub', { defaultValue: 'Each KPI vs the cohort average — green beats peers, red trails them.' })}</span>
        </div>
        <label className={styles.benchSelect}>
          <span className={styles.srOnly}>{t('supplier.kpi.bench.pick', { defaultValue: 'Supplier' })}</span>
          <select value={selected.supplier_id} onChange={(e) => setSelId(e.target.value)}>
            {rows.map((r) => <option key={r.supplier_id} value={r.supplier_id}>{r.name}</option>)}
          </select>
        </label>
      </div>

      <Stagger className={styles.benchKpis} step={50}>
        <StatCard label={t('supplier.kpi.bench.score', { defaultValue: 'Aggregate score' })} value={aggStats.sel ?? 0} decimals={0} accent="var(--spectro-purple, #9333EA)" />
        <StatCard label={t('supplier.kpi.bench.peer', { defaultValue: 'Peer average' })} value={aggStats.peer != null ? +aggStats.peer.toFixed(1) : 0} decimals={1} accent="var(--spectro-blue, #0065FF)" />
        <StatCard
          label={t('supplier.kpi.bench.percentile', { defaultValue: 'Cohort percentile' })}
          value={aggStats.percentile ?? 0}
          suffix={t('supplier.kpi.bench.pctSuffix', { defaultValue: 'th' })}
          accent={(aggStats.percentile ?? 0) >= 50 ? 'var(--color-status-success, #16A34A)' : 'var(--color-status-warning, #D97706)'}
          deltaCaption={t('supplier.kpi.bench.percentileHint', { defaultValue: 'vs {{n}} peers', n: rows.length })}
        />
      </Stagger>

      <div className={styles.tableScroll}>
        <table className={styles.benchTable}>
          <thead>
            <tr>
              <th>{t('supplier.kpi.bench.kpi', { defaultValue: 'KPI' })}</th>
              <th className={styles.numCol}>{selected.name}</th>
              <th className={styles.numCol}>{t('supplier.kpi.bench.peerCol', { defaultValue: 'Peer avg' })}</th>
              <th className={styles.numCol}>{t('supplier.kpi.bench.delta', { defaultValue: 'Δ vs peer' })}</th>
            </tr>
          </thead>
          <tbody>
            {KPI_KEYS.map((k) => {
              const val = selected.kpis[k]?.value ?? null;
              const peer = peerAvg[k] ?? null;
              const delta = val != null && peer != null ? val - peer : null;
              const higher = HIGHER_BETTER.has(k);
              const better = delta == null ? null : higher ? delta > 0 : delta < 0;
              return (
                <tr key={k}>
                  <td>{t(`supplier.detail.kpi.${k}.label`, { defaultValue: k })}</td>
                  <td className={styles.numCol}>{val != null ? nf.format(val) : '—'}</td>
                  <td className={`${styles.numCol} ${styles.subdued}`}>{peer != null ? nf.format(peer) : '—'}</td>
                  <td className={`${styles.numCol} ${better == null ? '' : better ? styles.deltaGood : styles.deltaBad}`}>
                    {delta == null ? '—' : `${delta > 0 ? '+' : ''}${nf.format(delta)}`}
                    {better != null && <span aria-hidden="true"> {better ? '▲' : '▼'}</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
